import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { sendBulkFaceEmail } from '../../../../UtilsEmail';

import {
  trackShareBulkShare,
  trackShareSampleShare,
} from '../../../../analytics';

import { Button } from '../../../../components/general';
import { URL, EMAILS } from '../../../../constants';

import useAuthUserStore from '../../../../hooks/useAuthUserStore';
import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { sendEmail } from '../../../../services/api/emails';

import { useProjectStore } from '../../../../stores/project';

import { Person, PeopleEmailState, SGTemplate } from '../../../../types';
import { isEmpty, getUrl, getShareLink } from '../../../../utils';

import SampleUpsellModal from './sampleUpsellModal';

interface Props {
  people: Person[];
  emailState: PeopleEmailState;
  setEmailState: React.Dispatch<React.SetStateAction<PeopleEmailState>>;
  creationTemplate: SGTemplate;
  afterEmailSend: () => void;
}

const MainShareBtn: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();

  const {
    people,
    emailState,
    setEmailState,
    creationTemplate,
    afterEmailSend,
  } = props;

  const { user } = useAuthUserStore();
  const { currentProject } = useProjectStore();

  const [isEmailsSending, setIsEmailsSending] = useState(false);
  const [showSampleUpsellModal, setShowSampleUpsellModal] = useState(false);

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);

  const shareBtnData = useMemo(() => {
    // sample project shares with user only
    if (isSample)
      return {
        title: 'Share',
        onClick: () => handleSampleShare(),
        disabled: false,
        popoverData: {
          header: 'Share to yourself',
          body: 'Share a personalised highlight reel with yourself to preview the email your attendees will receive',
        },
      };

    // no email template is selected
    if (isEmpty(creationTemplate))
      return {
        title: 'Select Template',
        onClick: () =>
          navigate(
            `${getUrl(URL.PROJECT_SETTINGS, eventId, 'email-hub/creation')}?redirectUrl=${getUrl(URL.VB_SHARE, eventId)}`,
          ),
        disabled: false,
        popoverData: {
          header: 'Select email template',
          body: 'Before sharing, select the email template the attendees will receive',
        },
      };

    // no attendee emails are provided
    if (Object.keys(emailState).every((id) => !emailState[id].email))
      return {
        title: 'Share to All',
        disabled: true,
        popoverData: {
          header: 'Share to all',
          body: 'Unable to share, as no attendee emails are provided. Fill in the emails below, and share either individually or in bulk',
        },
      };

    // can share to all
    return {
      title: 'Share to All',
      onClick: () => handleMainShare(),
      disabled: false,
      popoverData: {
        header: 'Share to all',
        body: 'Share personalised highlight reels to all with email below',
      },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isSample, creationTemplate, emailState]);

  if (!currentProject) return null;

  const handleMainShare = async () => {
    setIsEmailsSending(true);

    const { isSuccess } = await sendBulkFaceEmail({
      type: 'individual-creation',
      project: currentProject,
      people,
      emailState,
      setEmailState,
    });

    if (isSuccess) {
      afterEmailSend();
      trackShareBulkShare();
    }

    setIsEmailsSending(false);
  };

  const handleSampleShare = async () => {
    setIsEmailsSending(true);

    const face = people[0].face;

    const emailData = {
      recipientEmail: user.email,
      templateId: EMAILS.CREATION_SHARE,
      dynamicTemplateData: {
        event_name: currentProject.name,
        name: face.name,
        share_url: getShareLink(
          sampleProjectData.event.eventId,
          face.elementId,
        ),
      },
    };

    const isSuccess = await sendEmail(emailData);

    if (isSuccess) {
      setShowSampleUpsellModal(true);
      trackShareSampleShare();
    } else
      toast.error(
        <div>
          Email couldn't be sent to <b>{user.email}</b>, please try again later.
        </div>,
      );

    setIsEmailsSending(false);
  };

  return (
    <React.Fragment>
      <Button
        onClick={shareBtnData.onClick}
        variant="pill"
        colour="black"
        className="w-full sm:w-52"
        isSubmitBtn
        isProcessing={isEmailsSending}
        disabled={shareBtnData.disabled}
        allowPointerEvents
        popover={shareBtnData.popoverData}
      >
        {shareBtnData.title}
      </Button>

      {/* sample complete modal */}
      <SampleUpsellModal show={showSampleUpsellModal} />
    </React.Fragment>
  );
};

export default MainShareBtn;
